
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import { encryptPayload, decryptResponse } from "./../CryptoUtil";
import { api } from "./../../App";
import indiaData from "./../../data/location_data.json";

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  password: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
};

const AddZsm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);



useEffect(() => {
  if (!success) return;

  const timer = setTimeout(() => {
    setSuccess("");
  }, 3000);

  return () => clearTimeout(timer);
}, [success]);

  /*
  |--------------------------------------------------------------------------
  | Load states from location_data.json
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    try {
      const states =
        indiaData && typeof indiaData === "object"
          ? Object.keys(indiaData)
              .map((state) => state.trim())
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b))
          : [];

      setAvailableStates(states);

      if (!states.length) {
        setError("No working area states found in location data.");
      }
    } catch (err) {
      console.error("Unable to load location data:", err);
      setError("Failed to load working area states.");
    }
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Close dropdown when clicking outside
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Form field changes
  |--------------------------------------------------------------------------
  */
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setError("");
    setSuccess("");
  };

  /*
  |--------------------------------------------------------------------------
  | Open dropdown and focus search
  |--------------------------------------------------------------------------
  */
  const handleOpenDropdown = () => {
    setIsDropdownOpen((previous) => {
      if (!previous) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }

      return !previous;
    });
  };

  /*
  |--------------------------------------------------------------------------
  | Search states
  | IMPORTANT: No states appear until the user enters a search term.
  |--------------------------------------------------------------------------
  */
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredStates = normalizedSearch
    ? availableStates.filter((stateName) =>
        stateName.toLowerCase().includes(normalizedSearch)
      )
    : [];

  /*
  |--------------------------------------------------------------------------
  | Select / deselect state
  |--------------------------------------------------------------------------
  */
  const handleToggleState = (stateName) => {
    setSelectedStates((previous) =>
      previous.includes(stateName)
        ? previous.filter((state) => state !== stateName)
        : [...previous, stateName]
    );

    setError("");
    setSuccess("");
  };

  /*
  |--------------------------------------------------------------------------
  | Remove selected state
  |--------------------------------------------------------------------------
  */
  const handleRemoveState = (stateName) => {
    setSelectedStates((previous) =>
      previous.filter((state) => state !== stateName)
    );

    setError("");
    setSuccess("");
  };

  /*
  |--------------------------------------------------------------------------
  | Reset form
  |--------------------------------------------------------------------------
  */
  const handleReset = () => {
    setFormData({ ...INITIAL_FORM });
    setSelectedStates([]);
    setSearchTerm("");
    setIsDropdownOpen(false);
    setError("");
    setSuccess("");
  };

  /*
  |--------------------------------------------------------------------------
  | Submit form
  |--------------------------------------------------------------------------
  */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) return;

    setError("");
    setSuccess("");

    /*
    | Read existing Login.js session.
    */
    let session;

    try {
      const rawSession = localStorage.getItem("userSession");
      session = rawSession ? JSON.parse(rawSession) : null;
    } catch (err) {
      setError("Invalid login session. Please log in again.");
      return;
    }

    if (!session?.user) {
      setError("Admin login required. Please log in again.");
      return;
    }

    const role = String(
      session.user.role || session.user.userlevel || ""
    )
      .trim()
      .toUpperCase();

    const adminId = String(
      session.user.admin_id ||
        session.user.code ||
        session.user.id ||
        ""
    ).trim();

    const token = String(session.token || "").trim();

    if (role !== "ADMIN") {
      setError("Only ADMIN can add a ZSM.");
      return;
    }

    if (!/^AD[0-9]+$/i.test(adminId)) {
      setError("Valid Admin ID not found. Please log in again.");
      return;
    }

    if (!token) {
      setError("Login token missing. Please log in again.");
      return;
    }

    /*
    | Validate expiry only if login.php supplied one.
    */
    if (
      session.expiry !== null &&
      session.expiry !== undefined &&
      session.expiry !== ""
    ) {
      const expiryValue = session.expiry;

      const expiryMs =
        typeof expiryValue === "number"
          ? expiryValue < 100000000000
            ? expiryValue * 1000
            : expiryValue
          : Date.parse(String(expiryValue));

      if (Number.isFinite(expiryMs) && Date.now() >= expiryMs) {
        setError("Login session expired. Please log in again.");
        return;
      }
    }

    /*
    | Prepare request payload.
    */
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim(),
      password: formData.password,
      address: formData.address.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      pincode: formData.pincode.trim(),
      working_states: [...new Set(selectedStates)],
    };

    /*
    | Frontend validation.
    */
    if (!payload.name || payload.name.length > 100) {
      setError("Enter a valid name (maximum 100 characters).");
      return;
    }

    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email) ||
      payload.email.length > 150
    ) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!/^[0-9+\-\s()]{7,15}$/.test(payload.phone)) {
      setError("Please enter a valid phone number.");
      return;
    }

    if (payload.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (!payload.working_states.length) {
      setError("Please search and select at least one working state.");
      setIsDropdownOpen(true);
      return;
    }

    if (
      payload.address.length > 255 ||
      payload.city.length > 100 ||
      payload.state.length > 100 ||
      payload.pincode.length > 10
    ) {
      setError("One or more address fields exceed the allowed length.");
      return;
    }

    if (!api) {
      setError("API URL is not configured.");
      return;
    }

    setLoading(true);

    try {
      /*
      | Encrypt using existing CryptoUtil.js.
      */
      const encryptedRequest = encryptPayload(payload);

      /*
      | Send the existing PHP session cookie and Bearer token.
      */
      const endpoint = `${String(api).replace(/\/+$/, "")}/addZsm.php`;

      const response = await axios.post(endpoint, encryptedRequest, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        timeout: 15000,
      });

      /*
      | Decrypt the existing encrypted PHP response.
      */
      const encrypted = response.data?.encrypted;

      if (!encrypted?.payload || !encrypted?.transactionId) {
        throw new Error(
          response.data?.message || "Invalid encrypted server response."
        );
      }

      const result = decryptResponse(
        encrypted.payload,
        encrypted.transactionId
      );

      if (result?.success !== true) {
        throw new Error(result?.message || "Unable to add ZSM.");
      }

      /*
      | Success
      */
 
/* Success */
setFormData({ ...INITIAL_FORM });
setSelectedStates([]);
setSearchTerm("");
setIsDropdownOpen(false);
setError("");

setSuccess(result.message || "ZSM added successfully.");

  
    } catch (err) {
      console.error("Add ZSM failed:", {
        status: err.response?.status,
        message: err.message,
      });

      let message = "Unable to add ZSM. Please try again.";

      const encryptedError = err.response?.data?.encrypted;

      if (encryptedError?.payload && encryptedError?.transactionId) {
        try {
          const decoded = decryptResponse(
            encryptedError.payload,
            encryptedError.transactionId
          );

          message = decoded?.message || message;
        } catch (decryptError) {
          message = "Unable to decrypt the server error response.";
        }
      } else if (err.response) {
        message =
          err.response.data?.message ||
          `Server returned HTTP ${err.response.status}.`;
      } else if (err.code === "ECONNABORTED") {
        message =
          "Request timed out. Check whether the ZSM was created before retrying.";
      } else if (err.message) {
        message = err.message;
      }

      if (err.response?.status === 401) {
        message += " Your PHP login session may be missing or expired.";
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-12 grid-margin stretch-card">
        <div className="card add-zsm-page">
          <div className="card-body">
            {/* Page header */}
            <div className="add-zsm-header">
              <div>
                <h4 className="card-title">Add Zonal Sales Manager</h4>
                <p className="card-description">
                  Enter manager details and assign the working states.
                </p>
              </div>

              <span className="add-zsm-icon" aria-hidden="true">
                <i className="mdi mdi-account-plus-outline" />
              </span>
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="mdi mdi-alert-circle-outline me-2" />
                {error}
              </div>
            )}

            {/* {success && (
              <div className="alert alert-success" role="status">
                <i className="mdi mdi-check-circle-outline me-2" />
                {success}
              </div>
            )} */}

            <form
              className="forms-sample"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              {/* Personal information */}
              <div className="add-zsm-section">
                <h5 className="add-zsm-section-title">
                  <i className="mdi mdi-account-outline me-2" />
                  Personal Information
                </h5>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="zsm-name">ZSM Name *</label>
                      <input
                        id="zsm-name"
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter full name"
                        autoComplete="name"
                        maxLength={100}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="zsm-email">Email Address *</label>
                      <input
                        id="zsm-email"
                        type="email"
                        className="form-control"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="manager@example.com"
                        autoComplete="email"
                        maxLength={150}
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="zsm-phone">Phone Number *</label>
                      <input
                        id="zsm-phone"
                        type="tel"
                        className="form-control"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                        maxLength={15}
                        autoComplete="tel"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="zsm-password">Password *</label>
                      <input
                        id="zsm-password"
                        type="password"
                        className="form-control"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Minimum 6 characters"
                        minLength={6}
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Address information */}
              <div className="add-zsm-section">
                <h5 className="add-zsm-section-title">
                  <i className="mdi mdi-map-marker-outline me-2" />
                  Address Information
                </h5>

                <div className="row">
                  <div className="col-12">
                    <div className="form-group">
                      <label htmlFor="zsm-address">Address</label>
                      <input
                        id="zsm-address"
                        type="text"
                        className="form-control"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Enter complete address"
                        autoComplete="street-address"
                        maxLength={255}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="zsm-city">City</label>
                      <input
                        id="zsm-city"
                        type="text"
                        className="form-control"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Enter city"
                        autoComplete="address-level2"
                        maxLength={100}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="zsm-residential-state">
                        Residential State
                      </label>
                      <input
                        id="zsm-residential-state"
                        type="text"
                        className="form-control"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="Enter residential state"
                        autoComplete="address-level1"
                        maxLength={100}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="zsm-pincode">Pincode</label>
                      <input
                        id="zsm-pincode"
                        type="text"
                        className="form-control"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        placeholder="Enter pincode"
                        maxLength={10}
                        autoComplete="postal-code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Working area */}
              
{/* Working Area */}

{/* Working Area */}
<div className="add-zsm-section">
  <div className="card">
    <div className="card-body">
      {/* Heading */}
      <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
        <div>
          <h4 className="card-title mb-2">
            <i className="mdi mdi-map-marker-multiple-outline text-primary mr-2" />
            Working Area
          </h4>

          <p className="card-description mb-0">
            Search and select the states where this ZSM will work.
          </p>
        </div>

        <span className="badge badge-primary">
          {selectedStates.length} selected
        </span>
      </div>

      {/* Selected states */}
      {selectedStates.length > 0 && (
        <div className="form-group">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="mb-0">
              Selected States
            </label>

            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => {
                setSelectedStates([]);
                setError("");
                setSuccess("");
              }}
              disabled={loading}
            >
              <i className="mdi mdi-close-circle-outline mr-1" />
              Clear all
            </button>
          </div>

          <div className="d-flex flex-wrap">
            {selectedStates.map((stateName) => (
              <span
                key={stateName}
                className="badge badge-info mr-2 mb-2 p-2"
              >
                {stateName}

                <button
                  type="button"
                  className="btn btn-link text-white p-0 ml-2"
                  onClick={() => handleRemoveState(stateName)}
                  disabled={loading}
                  title={`Remove ${stateName}`}
                  aria-label={`Remove ${stateName}`}
                >
                  <i className="mdi mdi-close" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="form-group">
        <label htmlFor="zsm-working-area-search">
          Search State
        </label>

        <div className="input-group">
          <div className="input-group-prepend">
            <span className="input-group-text">
              <i className="mdi mdi-magnify" />
            </span>
          </div>

          <input
            id="zsm-working-area-search"
            type="search"
            className="form-control"
            placeholder="Type a state name..."
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setError("");
            }}
            disabled={loading}
            autoComplete="off"
          />

          {searchTerm && (
            <div className="input-group-append">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setSearchTerm("")}
                disabled={loading}
                aria-label="Clear search"
              >
                <i className="mdi mdi-close" />
              </button>
            </div>
          )}
        </div>

        <small className="form-text text-muted">
          Type a state name to display matching options.
        </small>
      </div>

      {/* State list */}
      {!normalizedSearch ? (
        <div className="alert alert-light">
          <i className="mdi mdi-information-outline mr-2" />
          Search for a state to get started.
        </div>
      ) : filteredStates.length > 0 ? (
        <div className="border rounded p-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
            <h5 className="mb-0">Available States</h5>

            <small className="text-muted">
              {filteredStates.length} result(s)
            </small>
          </div>

          <div className="row">
            {filteredStates.map((stateName) => {
              const isSelected = selectedStates.includes(stateName);
              const checkboxId = `zsm-state-${stateName.replace(
                /[^a-zA-Z0-9]/g,
                "-"
              )}`;

              return (
                <div className="col-md-6 mb-2" key={stateName}>
                  <div className="form-group mb-0">
                    <div className="form-check form-check-primary">
                      <input
                        id={checkboxId}
                        type="checkbox"
                        className="form-check-input"
                        checked={isSelected}
                        disabled={loading}
                        onChange={() => {
                          setSelectedStates((previousStates) => {
                            if (previousStates.includes(stateName)) {
                              return previousStates.filter(
                                (state) => state !== stateName
                              );
                            }

                            return [...previousStates, stateName];
                          });

                          setError("");
                          setSuccess("");
                        }}
                      />

                      <label
                        className="form-check-label"
                        htmlFor={checkboxId}
                      >
                        {stateName}
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="alert alert-warning">
          <i className="mdi mdi-alert-outline mr-2" />
          No matching states found. Try another state name.
        </div>
      )}

      {/* Help text */}
      <p className="card-description mt-3 mb-0">
        <i className="mdi mdi-information-outline mr-1" />
        Select multiple states if required. Your selections remain
        selected while searching for other states.
      </p>
    </div>
  </div>
</div>



              {/* Actions */}
              <div className="add-zsm-actions">
                <button
                  type="button"
                  className="btn btn-light"
                  disabled={loading}
                  onClick={handleReset}
                >
                  <i className="mdi mdi-refresh me-1" />
                  Reset
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        aria-hidden="true"
                      />
                      Adding ZSM...
                    </>
                  ) : (
                    <>
                      <i className="mdi mdi-account-plus me-1" />
                      Add ZSM
                    </>
                  )}
                </button>

                {success && (
  <div
    className="alert alert-success add-zsm-success-message mt-4 mb-0"
    role="status"
    aria-live="polite"
  >
    <i className="mdi mdi-check-circle-outline me-2" />
    <strong>{success}</strong>
  </div>
)}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddZsm;
